import { useMemo, useState } from 'react'
import { Button, Popover, Skeleton, Spinner } from '@heroui/react'
import { Link } from 'react-router-dom'

import { AlertIcon, AlertTriangleIcon, BellIcon, InfoIcon } from '@/components/ui/icons'
import { useSystemNotices } from '@/features/notifications/hooks'
import { formatNoticeTime } from '@/features/notifications/relative-time'
import type { NoticeLevel, SystemNotice } from '@/features/notifications/types'

/** Icon, tint and the word itself. Colour never carries a level on its own. */
const levels: Record<NoticeLevel, { icon: typeof InfoIcon; className: string; label: string }> = {
  info: { icon: InfoIcon, className: 'text-brand-600', label: 'Notice' },
  warning: { icon: AlertTriangleIcon, className: 'text-warning', label: 'Warning' },
  critical: { icon: AlertIcon, className: 'text-danger', label: 'Urgent' },
}

/**
 * System notices, reachable from every page.
 *
 * It lives in the app bar rather than on the dashboard because a notice about
 * the account is worth seeing from wherever the merchant is standing — and a
 * dashboard-only feed is invisible to anyone who works inside one module all
 * day.
 */
export function NotificationBell() {
  const {
    notices,
    unreadCount,
    isLoading,
    isUnavailable,
    isError,
    retry,
    markAllRead,
    isMarkingRead,
  } = useSystemNotices()
  const [isOpen, setIsOpen] = useState(false)

  const label = unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'

  return (
    <Popover isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button variant="ghost" size="sm" isIconOnly aria-label={label} className="relative">
        <BellIcon width={18} height={18} />

        {unreadCount > 0 && (
          // The count is already in the button's label, so the pill itself is
          // decoration and stays out of the accessibility tree.
          <span
            aria-hidden
            className="bg-brand-500 absolute -top-0.5 -right-0.5 flex min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-4 font-semibold text-white"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* React Aria portals overlays to `document.body`, outside the shell — so
          the brand token scope has to travel with the panel. */}
      <Popover.Content placement="bottom end" offset={10} className="overlay-scope">
        <Popover.Dialog className="w-[min(22rem,calc(100vw-2rem))] p-0">
          <header className="border-border flex items-center justify-between gap-3 border-b px-4 py-3">
            <Popover.Heading className="text-sm font-semibold tracking-tight">
              Notifications
            </Popover.Heading>

            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                disabled={isMarkingRead}
                className="text-brand-600 focus-visible:ring-brand-500 cursor-pointer rounded text-xs font-medium outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isMarkingRead ? <Spinner size="sm" /> : 'Mark all read'}
              </button>
            )}
          </header>

          <PanelBody
            notices={notices}
            isLoading={isLoading}
            isUnavailable={isUnavailable}
            isError={isError}
            onRetry={retry}
            onNavigate={() => setIsOpen(false)}
          />
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  )
}

interface PanelBodyProps {
  notices: SystemNotice[]
  isLoading: boolean
  isUnavailable: boolean
  isError: boolean
  onRetry: () => void
  onNavigate: () => void
}

function PanelBody({
  notices,
  isLoading,
  isUnavailable,
  isError,
  onRetry,
  onNavigate,
}: PanelBodyProps) {
  // One clock read per opening: React Aria unmounts a popover's contents while
  // it is closed, so this remounts each time the panel is opened. Reading the
  // clock per row instead would let two notices a second apart disagree about
  // which minute it is.
  const now = useMemo(() => new Date(), [])

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 px-4 py-4" aria-busy>
        <span className="sr-only">Loading notifications…</span>
        {[0, 1, 2].map((row) => (
          <div key={row} className="flex gap-3">
            <Skeleton className="size-5 shrink-0 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3.5 w-3/4 rounded" />
              <Skeleton className="h-3 w-1/3 rounded" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <PanelMessage title="Couldn't load notifications">
        <button
          type="button"
          onClick={onRetry}
          className="text-brand-600 focus-visible:ring-brand-500 mt-2 cursor-pointer rounded text-xs font-medium underline underline-offset-2 outline-none focus-visible:ring-2"
        >
          Try again
        </button>
      </PanelMessage>
    )
  }

  if (isUnavailable) {
    return (
      <PanelMessage title="No notifications yet">
        Account and service messages from NexQR will appear here.
      </PanelMessage>
    )
  }

  if (notices.length === 0) {
    return <PanelMessage title="You're all caught up">Nothing needs your attention.</PanelMessage>
  }

  return (
    <ul className="divide-border max-h-96 divide-y overflow-y-auto">
      {notices.map((notice) => (
        <NoticeRow key={notice.id} notice={notice} now={now} onNavigate={onNavigate} />
      ))}
    </ul>
  )
}

function PanelMessage({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="px-4 py-8 text-center">
      <p className="text-sm font-medium">{title}</p>
      {children && (
        <p className="text-muted mx-auto mt-1.5 max-w-[32ch] text-xs leading-relaxed">{children}</p>
      )}
    </div>
  )
}

function NoticeRow({
  notice,
  now,
  onNavigate,
}: {
  notice: SystemNotice
  now: Date
  onNavigate: () => void
}) {
  const level = levels[notice.level] ?? levels.info
  const Icon = level.icon
  const isUnread = !notice.readAt

  const body = (
    <>
      <span className={`mt-0.5 shrink-0 ${level.className}`}>
        <Icon width={17} height={17} />
      </span>

      <span className="min-w-0 flex-1">
        {/* The level is spoken, not just tinted — the icon carries it in
            greyscale and this carries it to a screen reader. */}
        <span className="sr-only">{level.label}: </span>
        <span className={`block text-[13px] leading-snug ${isUnread ? 'font-semibold' : ''}`}>
          {notice.title}
        </span>

        {notice.body && (
          <span className="text-muted mt-0.5 block text-xs leading-relaxed">{notice.body}</span>
        )}

        <span className="text-muted mt-1 block text-[11px]">
          <time dateTime={notice.createdAt}>{formatNoticeTime(notice.createdAt, now)}</time>
        </span>
      </span>

      {isUnread && (
        <>
          <span aria-hidden className="bg-brand-500 mt-1.5 size-1.5 shrink-0 rounded-full" />
          <span className="sr-only">Unread</span>
        </>
      )}
    </>
  )

  const shell = 'flex gap-3 px-4 py-3'

  if (notice.href) {
    return (
      <li>
        <Link
          to={notice.href}
          onClick={onNavigate}
          className={`${shell} hover:bg-surface-hover focus-visible:ring-brand-500 cursor-pointer no-underline outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-inset`}
        >
          {body}
        </Link>
      </li>
    )
  }

  return <li className={shell}>{body}</li>
}
